import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import Balota from '../../../common/components/Balota/Balota';
import { BALOTA_SIZES } from '../../../common/components/Balota/constants';
import { TICKET_STATUS } from '../../../core/constants/lottery';
import { ROUTES } from '../../../core/constants/routes';
import { slotStartAt } from '../../../core/lottery/draw';
import { formatCurrency, formatDateTime } from '../../../core/utils/format';
import { useTickets } from '../../../global/contexts/lottery/tickets';
import { groupBySlot } from '../utils/group';

import '../styles/overview.css';

const STATUS_LABEL = {
    [TICKET_STATUS.PENDING]: 'En juego',
    [TICKET_STATUS.WON]:     '¡Ganaste!',
    [TICKET_STATUS.LOST]:    'No salió',
};

function TicketCard({ ticket }) {
    return (
        <article className={`ticket-card ticket-card--${ticket.status}`}>
            <div className="ticket-card__balls">
                {ticket.number.split('').map((digit, index) => (
                    <Balota
                        key={index}
                        digit={digit}
                        size={BALOTA_SIZES.SM}
                        muted={ticket.status === TICKET_STATUS.LOST}
                    />
                ))}
            </div>

            <div className="ticket-card__body">
                <span className={`ticket-card__status ticket-card__status--${ticket.status}`}>
                    {STATUS_LABEL[ticket.status]}
                </span>
                <span className="ticket-card__meta">
                    Ref {ticket.paymentReference} · {formatCurrency(ticket.price)}
                </span>
            </div>

            {ticket.status === TICKET_STATUS.WON && (
                <strong className="ticket-card__prize">{formatCurrency(ticket.prize)}</strong>
            )}
        </article>
    );
}

function DrawGroup({ group }) {
    const [firstTicket] = group.items;
    const { result, status } = firstTicket;

    return (
        <section className="tickets-overview__group">
            <header className="tickets-overview__group-head">
                <h2 className="tickets-overview__group-title">
                    Sorteo del {formatDateTime(slotStartAt(group.slotIndex))}
                </h2>
                {result ? (
                    <span className="tickets-overview__result">
                        Resultado <strong>{result.number}</strong>
                    </span>
                ) : (
                    <span className="tickets-overview__result tickets-overview__result--pending">
                        {status === TICKET_STATUS.PENDING ? 'Aún no se juega' : ''}
                    </span>
                )}
            </header>

            <div className="tickets-overview__cards">
                {group.items.map(ticket => <TicketCard key={ticket.id} ticket={ticket} />)}
            </div>
        </section>
    );
}

function StatTile({ label, value, tone }) {
    return (
        <div className={`tickets-overview__stat ${tone ? `tickets-overview__stat--${tone}` : ''}`}>
            <span className="tickets-overview__stat-label">{label}</span>
            <strong className="tickets-overview__stat-value">{value}</strong>
        </div>
    );
}

export default function TicketsOverview() {
    const { tickets, stats, reset } = useTickets();

    const groups = useMemo(() => groupBySlot(tickets), [tickets]);

    const handleReset = () => {
        reset();
        toast.success('Se borraron tus números de esta sesión');
    };

    if (!tickets.length) {
        return (
            <section className="tickets-overview section">
                <div className="tickets-overview__empty panel">
                    <span className="tickets-overview__empty-icon" aria-hidden="true">🎟️</span>
                    <h1 className="section__title">Todavía no tienes números</h1>
                    <p className="section__subtitle">
                        Elige tu combinado en la tienda y aparecerá aquí con el resultado
                        del sorteo en el que juega.
                    </p>
                    <Link className="button button--primary" to={ROUTES.STORE}>
                        Ir a la tienda
                    </Link>
                </div>
            </section>
        );
    }

    return (
        <section className="tickets-overview section">
            <div className="section__head">
                <div>
                    <h1 className="section__title">Mis números</h1>
                    <p className="section__subtitle">
                        Cada número juega en el sorteo que estaba abierto cuando lo compraste.
                    </p>
                </div>
                <Link className="button button--primary" to={ROUTES.STORE}>Comprar más</Link>
            </div>

            <div className="tickets-overview__stats">
                <StatTile label="Números" value={stats.total} />
                <StatTile label="En juego" value={stats.pending} />
                <StatTile label="Acertados" value={stats.won} tone={stats.won ? 'won' : undefined} />
                <StatTile label="Invertido" value={formatCurrency(stats.spent)} />
                <StatTile
                    label="Ganado"
                    value={formatCurrency(stats.winnings)}
                    tone={stats.winnings ? 'won' : undefined}
                />
            </div>

            {groups.map(group => <DrawGroup key={group.slotIndex} group={group} />)}

            <button type="button" className="tickets-overview__reset" onClick={handleReset}>
                Reiniciar la demo (borra mis números)
            </button>
        </section>
    );
}
